import { FC, ReactNode, useCallback, useMemo } from 'react';
import { colorGray } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Flex } from 'antd';
import * as Icon from 'react-feather';
import styled from 'styled-components';
import { Detail } from '../../common/components';
import { getValidPeriodString } from '../../common/utils';
import { DataObjectDescriptionVersionStore } from '../DataObjectDescriptionVersionStore';
import { DataObjectOutputType } from '../types';
import { useIsDimensionWithDataType } from './joins/utils';
import { getAlgorithmMappingLabel } from './mappings/utils';
import { AlgorithmExtendedType } from './schema/types';
import { AlgorithmSourceFormDataType } from './sources/schema/types';
import {
  AlgorithmMapping,
  getAlgorithmJoinLabels,
  getAlgorithmSourceLabel,
  getAlgorithmTargetMappingRuleLabels,
  getAlgorithmTargetRuleRuleLabels,
  getConfidentialityRuleSourceLabel,
} from './utils';
import translations from './AlgorithmInfo.translations.json';
import { AlgorithmSequenceNumber } from './AlgorithmSequenceNumber';

const { addTranslations, useTranslation } = i18nNsFactory('algorithm-info');
addTranslations(translations);

export interface AlgorithmInfoProps {
  algorithm: AlgorithmExtendedType;
  sourcesById: Map<number, AlgorithmSourceFormDataType>;
  mappingsById: Map<number, AlgorithmMapping>;
  alternativeCode?: number;
  outputType?: DataObjectOutputType | null;
  showTargetEdit: boolean;
  renderRow?: RenderRow;
}

export const AlgorithmInfo: FC<AlgorithmInfoProps> = (props) => {
  const {
    algorithm,
    sourcesById,
    alternativeCode,
    outputType,
    showTargetEdit,
    renderRow = defaultRenderRow,
  } = props;
  const { confidentialityRule, sources } = algorithm;
  const { t } = useTranslation();
  const dataObjectDescriptionVersion =
    DataObjectDescriptionVersionStore.useOptionalValue(
      'dataObjectDescriptionVersion',
    );

  const isDatabaseScript = Boolean(algorithm.expression);
  const isReport =
    (outputType ?? dataObjectDescriptionVersion?.outputTypeCode) ===
    DataObjectOutputType.REPORT;

  const openTarget = useCallback(
    (targetId: number) => {
      const currPath = window.location.pathname;
      const searchParams = new URLSearchParams({
        segment: 'algorithms',
        algorithmId: algorithm.id + '',
        targetId: targetId + '',
      });
      window.open(currPath + '?' + searchParams.toString(), '_blank');
    },
    [algorithm.id],
  );

  const sourcesValue = useMemo(
    () => sources.map(getAlgorithmSourceLabel).join(', '),
    [sources],
  );

  const mappingsValue = useMemo(
    () =>
      algorithm.mappings
        .map((mapping) => getAlgorithmMappingLabel(mapping, sourcesById))
        .join(', '),
    [algorithm.mappings, sourcesById],
  );

  const isDimensionWithDateDataType = useIsDimensionWithDataType('DATE');

  const joinsValue = useMemo(
    () =>
      getAlgorithmJoinLabels(
        algorithm.joins,
        isDimensionWithDateDataType,
        sourcesById,
      ).join(' '),
    [algorithm.joins, sourcesById, isDimensionWithDateDataType],
  );

  return (
    <Flex vertical gap="small">
      {renderRow(
        'type',
        algorithm.typeCode,
        <Detail label={t('type.label')}>
          {t('type.' + algorithm.typeCode)}
        </Detail>,
      )}
      {renderRow(
        'group',
        algorithm.groupCode,
        <Detail label={t('group')}>{algorithm.groupCode}</Detail>,
      )}
      {typeof alternativeCode === 'number' &&
        renderRow(
          'alternative',
          alternativeCode,
          <Detail label={t('alternative')}>{alternativeCode}</Detail>,
        )}
      {renderRow(
        'sequenceNumber',
        algorithm.sequenceNumber,
        <Detail label={t('sequenceNumber')}>
          <AlgorithmSequenceNumber
            sequenceNumber={algorithm.sequenceNumber}
            execAfterAggregation={algorithm.execAfterAggregation}
          />
        </Detail>,
      )}
      {renderRow(
        'validPeriod',
        getValidPeriodString(algorithm.validPeriod),
        <Detail label={t('validPeriod')}>
          {getValidPeriodString(algorithm.validPeriod)}
        </Detail>,
      )}
      {renderRow(
        'description',
        algorithm.description,
        <Detail label={t('description')}>{algorithm.description}</Detail>,
      )}
      {isDatabaseScript &&
        renderRow(
          'expression',
          algorithm.expression,
          <Detail label={t('expression')}>{algorithm.expression}</Detail>,
        )}
      {renderRow(
        'aggregatePeriods',
        algorithm.aggregatePeriods ? t('yes') : t('no'),
        <Detail label={t('aggregatePeriods')}>
          {algorithm.aggregatePeriods ? t('yes') : t('no')}
        </Detail>,
      )}
      {confidentialityRule &&
        renderRow(
          'confidentialityRule',
          algorithm.confidentialityRule,
          <Detail label={t('confidentialityRule')}>
            {getConfidentialityRuleSourceLabel(confidentialityRule, sources)}
          </Detail>,
        )}
      {sourcesValue &&
        renderRow(
          'sources',
          sourcesValue,
          <Detail label={t('sources', { count: algorithm.sources.length })}>
            {sourcesValue}
          </Detail>,
        )}
      {mappingsValue &&
        renderRow(
          'mappings',
          mappingsValue,
          <Detail label={t('mappings', { count: algorithm.mappings.length })}>
            {mappingsValue}
          </Detail>,
        )}
      {joinsValue &&
        renderRow(
          'joins',
          joinsValue,
          <Detail label={t('joins', { count: algorithm.joins.length - 1 })}>
            {joinsValue}
          </Detail>,
        )}
      {!isDatabaseScript &&
        renderRow(
          'filterClause',
          algorithm.filterClause,
          <Detail label={t('filterClause')}>{algorithm.filterClause}</Detail>,
        )}
      {algorithm.targets.length > 0 && (
        <Flex vertical gap="middle">
          {renderRow(
            'targets',
            algorithm.targets,
            <strong>
              {t('targets.label', { count: algorithm.targets.length })}:
            </strong>,
          )}
          {algorithm.targets.map((target, index) => {
            const rulesValue = [
              ...getAlgorithmTargetMappingRuleLabels(target.rules, sourcesById),
              ...getAlgorithmTargetRuleRuleLabels(target.rules),
            ].join(', ');

            return renderRow(
              `targets.${index}`,
              target,
              <TargetContainer vertical key={index} gap="small">
                <Flex justify="space-between">
                  <strong>
                    {t('targets.targetLabel', {
                      index: target.sequenceNumber,
                    })}
                  </strong>
                  {showTargetEdit && (
                    <Button
                      id={'edit-algorithm-target-' + index}
                      className="edit-algorithm-target-button"
                      icon={<Icon.Edit3 size={18} />}
                      onClick={() => openTarget(target.id)}
                    />
                  )}
                </Flex>
                <Flex vertical>
                  {renderRow(
                    `targets.${index}.condition`,
                    target.condition,
                    <Detail label={t('targets.condition')}>
                      {target.condition}
                    </Detail>,
                  )}
                  {isReport &&
                    renderRow(
                      `targets.${index}.targetKey`,
                      target.targetKey,
                      <Detail label={t('targets.targetKey')}>
                        {target.targetKey}
                      </Detail>,
                    )}
                  {renderRow(
                    `targets.${index}.description`,
                    target.description,
                    <Detail label={t('targets.description')}>
                      {target.description}
                    </Detail>,
                  )}
                  {renderRow(
                    `targets.${index}.rules`,
                    rulesValue,
                    <Detail label={t('targets.rules')}>{rulesValue}</Detail>,
                  )}
                </Flex>
              </TargetContainer>,
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

export type RenderRow = (
  key: string,
  value: any,
  defaultNode: ReactNode,
) => ReactNode;

const defaultRenderRow: RenderRow = (_key, _value, defaultNode) => defaultNode;

const TargetContainer = styled(Flex)`
  padding: 8px;
  margin: -8px;

  .edit-algorithm-target-button {
    display: none;
    margin-right: 4px;
    margin-bottom: -10px;
  }

  &:hover {
    background-color: ${colorGray[2]};
    border-radius: 12px;

    .edit-algorithm-target-button {
      display: initial;
    }
  }
`;
